import { useContext, useState } from 'react';
import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@playcastdotio/Shadcn';
import { PlaycastContext } from '../PlaycastWrapper';
import { captureResolutionState, type CaptureResolution } from '../../api/stores/shared';
import { refreshDisplayShadowcamStreams } from '../../api/modules/managers/mediaManager';
import { mediaCoordinator } from '../../coordinators/init';
import { USE_MEDIA_COORDINATOR } from '../../config/featureFlags';

// The virtual ShadowCams Playjector exposes. The selected camera drives both the studio preview and
// which camera the resolution override applies to.
const CAMERAS = [
  'ShadowCam00',
  'ShadowCam01',
  'ShadowCam02',
  'ShadowCam03',
  'ShadowCam04',
  'ShadowCam05',
  'ShadowCam06',
  'ShadowCam07',
  'ShadowCam08',
];

// Only ShadowCam's fixed DirectShow catalog sizes actually renegotiate the render size. Arbitrary
// sizes would fall back to the closest catalog entry (usually 1080p), so we expose the catalog only.
// These must stay in sync with s_kCaps in CommonLib/include/Video.h. The filter auto-derives every
// portrait (9:16) size by swapping the landscape entry's width/height, so both orientations are valid.
const RESOLUTIONS: { label: string; width: number; height: number }[] = [
  // Landscape 16:9
  { label: '3840x2160 (2160p / 4K)', width: 3840, height: 2160 },
  { label: '2560x1440 (1440p)', width: 2560, height: 1440 },
  { label: '1920x1080 (1080p)', width: 1920, height: 1080 },
  { label: '1600x900', width: 1600, height: 900 },
  { label: '1280x720 (720p)', width: 1280, height: 720 },
  { label: '1024x576', width: 1024, height: 576 },
  { label: '960x540 (qHD)', width: 960, height: 540 },
  { label: '640x480 (480p / 4:3)', width: 640, height: 480 },
  { label: '640x360 (nHD)', width: 640, height: 360 },
  { label: '480x270', width: 480, height: 270 },
  { label: '320x180', width: 320, height: 180 },
  // Portrait 9:16 (auto-derived counterparts)
  { label: '2160x3840 (portrait 4K)', width: 2160, height: 3840 },
  { label: '1440x2560 (portrait 1440p)', width: 1440, height: 2560 },
  { label: '1080x1920 (portrait 1080p)', width: 1080, height: 1920 },
  { label: '900x1600 (portrait)', width: 900, height: 1600 },
  { label: '720x1280 (portrait 720p)', width: 720, height: 1280 },
  { label: '576x1024 (portrait)', width: 576, height: 1024 },
  { label: '540x960 (portrait qHD)', width: 540, height: 960 },
  { label: '480x640 (portrait 4:3)', width: 480, height: 640 },
  { label: '360x640 (portrait nHD)', width: 360, height: 640 },
  { label: '270x480 (portrait)', width: 270, height: 480 },
  { label: '180x320 (portrait)', width: 180, height: 320 },
];

const resKey = (res: CaptureResolution) => `${res.width}x${res.height}`;

// Dev menu (Ctrl+F12) control for the studio preview camera and the resolution the host requests when
// opening it via getUserMedia. The resolution is applied as an exact/required constraint so Playjector
// renders that camera to match. Picking a resolution stores a per-camera override and reopens the
// camera so the DirectShow pin renegotiates (close + open); the preview re-attaches automatically.
export const CameraResolutionControl = () => {
  const context = useContext(PlaycastContext);
  // Bump to force a re-read of the (non-context) resolution store after we mutate it.
  const [, forceRender] = useState(0);

  // The preview camera (hostRuleState) is the single source of truth for which camera we're working
  // with, so switching it here also switches the live preview.
  const selectedCamera = context.stateStores.hostRuleState.videoPreviewCamera || 'ShadowCam03';
  const override = captureResolutionState.currentState.overrides[selectedCamera];
  const currentValue = override ? resKey(override) : undefined;

  const reopenSelectedCamera = () => {
    // Close + reopen so the new required resolution renegotiates via DirectShow. In prime mode the
    // legacy mediaManager path is active (USE_MEDIA_COORDINATOR === false); otherwise MediaCoordinator.
    if (USE_MEDIA_COORDINATOR) {
      void mediaCoordinator.restartShadowcamCapture(selectedCamera);
    } else {
      void refreshDisplayShadowcamStreams(true);
    }
  };

  const handleCameraChange = (camera: string) => {
    // Switch the live preview to this camera (also opens it if needed). selectedCamera reflects the
    // new value on the next render via hostRuleState.
    context.electronFunctions?.switchVideoPreviewCamera(camera);
  };

  const applyResolution = (value: string) => {
    const res = RESOLUTIONS.find((r) => resKey(r) === value);
    if (!res) return;

    const overrides = {
      ...captureResolutionState.currentState.overrides,
      [selectedCamera]: { width: res.width, height: res.height },
    };
    captureResolutionState.updateState({ overrides });
    forceRender((v) => v + 1);
    reopenSelectedCamera();
  };

  const clearOverride = () => {
    const overrides = { ...captureResolutionState.currentState.overrides };
    if (!(selectedCamera in overrides)) return;
    delete overrides[selectedCamera];
    captureResolutionState.updateState({ overrides });
    forceRender((v) => v + 1);
    reopenSelectedCamera();
  };

  return (
    <div className='flex flex-col space-y-4 rounded-lg border border-gray-700 p-4'>
      <h3 className='text-lg font-semibold'>ShadowCam Preview & Capture Resolution</h3>

      <div className='flex flex-col space-y-2'>
        <label className='text-sm font-medium'>Camera</label>
        <div className='flex items-center space-x-2'>
          <Select value={selectedCamera} onValueChange={handleCameraChange}>
            <SelectTrigger>
              <SelectValue placeholder='Select a camera' />
            </SelectTrigger>
            <SelectContent className='z-[10000]'>
              {CAMERAS.map((camera) => (
                <SelectItem key={camera} value={camera}>
                  {camera}
                  {captureResolutionState.currentState.overrides[camera]
                    ? ` — ${resKey(captureResolutionState.currentState.overrides[camera])}`
                    : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant='default' size='sm' onClick={() => context.targetFunctions.showVideoPreview()}>
            Toggle Preview
          </Button>
        </div>
      </div>

      <div className='flex flex-col space-y-2'>
        <label className='text-sm font-medium'>Resolution</label>
        <div className='flex items-center space-x-2'>
          <Select value={currentValue} onValueChange={applyResolution}>
            <SelectTrigger>
              <SelectValue placeholder='Default (no override)' />
            </SelectTrigger>
            <SelectContent className='z-[10000]'>
              {RESOLUTIONS.map((res) => (
                <SelectItem key={resKey(res)} value={resKey(res)}>
                  {res.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant='outline' size='sm' disabled={!override} onClick={clearOverride}>
            Clear
          </Button>
        </div>
      </div>

      <div className='text-xs text-gray-500'>
        <p>Selecting a camera switches the studio preview to it. The resolution is applied as the exact (required) getUserMedia size for that camera; Playjector renders to match. Only the listed catalog sizes are supported.</p>
        <p>Changing the resolution reopens the camera and the preview refreshes automatically. "Clear" restores default behavior. ShadowCam03 defaults to 720p.</p>
      </div>
    </div>
  );
};
